
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../../components/docs/Actions';
import Docs from 'plaid-threads/Icons/Docs';
import Globe from 'plaid-threads/Icons/Globe';
import Rocket from 'plaid-threads/Icons/Rocket';
import Grid from 'plaid-threads/Grid';
import Column from 'plaid-threads/Column';
import Layout from '../../../../components/docs/Layout';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';
import { CodeSnippet } from '../../../../components/Surfaces';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: "Set up an account linking flow that uses Plaid to connect your user's bank account. Then, use the obtained access token to improve account funding and withdrawals."
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const StepWithDiagram = makeShortcode("StepWithDiagram");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='User onboarding and account funding' subtitle='Use Plaid to simplify your onboarding and funding flow in Europe' mdxType="Header" />
    <p>{`In this guide, we first walk through setting up an account linking flow that uses Plaid to connect your user's bank account. We'll then demonstrate how to verify the identity of your users which may help comply with KYC and AML regulations.`}</p>
    <p>{`Finally, we'll show how to initiate payments while requiring that bank accounts involved in money movements are verified.`}</p>
    <p>{`If you're looking for a guide that is specific to Plaid's One-Time Payment Initiation product and does not incorporate other features such as anti-fraud measures or Identity Verification, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time"
      }}>{`Add One-time Payment Initiation to your App`}</a>{`.`}</p>
    <p>{`This guide is specific to integrations serving end users in Europe (including non-EU countries such as the UK and Norway). For US payment solutions, see `}<a parentName="p" {...{
        "href": "/docs/transfer"
      }}>{`Transfer`}</a>{`.`}</p>
    <h4 {...{
      "id": "before-you-get-started"
    }}>{`Before you get started`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "/docs/auth/"
      }}>{`Auth`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/identity/"
      }}>{`Identity`}</a>{`, and `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/"
      }}>{`Payments (Europe)`}</a>{` products covered in this guide can immediately be tried out in the Sandbox environment, which uses test data and does not interact with financial institutions.`}</p>
    <p>{`To gain access to these products in the Production environments, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/products"
      }}>{`request product access`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{`. If you already are using Plaid products, you can also request access by contacting your account manager or submitting a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
      }}>{`support ticket`}</a>{`.`}</p>
    <h3 {...{
      "id": "user-onboarding"
    }}>{`User onboarding`}</h3>
    <StepWithDiagram asset='/assets/img/docs/payment_initiation/PIS-overview-row-0.png' hideBorder={true} mdxType="StepWithDiagram">
  The <b>Plaid flow</b> begins when the user registers with your app. The flow
  will connect your app to your user's financial institution.
    </StepWithDiagram>
    <StepWithDiagram step={1} asset='/assets/img/docs/link-tokens/link-token-row-2.png' mdxType="StepWithDiagram">
  Your backend creates a <code>link_token</code> by using the
  <a href='/docs/api/link/#linktokencreate'>
    <code>/link/token/create</code>
  </a> endpoint and passes the temporary token to your Client app.
    </StepWithDiagram>
    <StepWithDiagram step={2} asset='/assets/img/docs/payment_initiation/PIS-overview-row-2.png' mdxType="StepWithDiagram">
  Your Client app uses the <code>link_token</code> to initiate a Link flow for
  your user. The{' '}
  <a href='/docs/link/web/#onsuccess'>
    <code>onSuccess</code> callback
  </a>{' '}
  signals that the user has granted your app access to their financial
  institution.
    </StepWithDiagram>
    <StepWithDiagram step={3} asset='/assets/img/docs/link-tokens/link-token-row-4.png' mdxType="StepWithDiagram">
  The <code>onSuccess</code> payload contains a{' '}
  <a href='/docs/link/web/#link-web-onsuccess-public-token'>
    <code>public_token</code>
  </a>{' '}
  which, after sending it to your backend, needs to be exchanged for a
  long-lived access token using the{' '}
  <a href='/docs/api/items/#itempublic_tokenexchange'>
    <code>/item/public_token/exchange</code>
  </a>{' '}
  endpoint.
    </StepWithDiagram>
    <StepWithDiagram step={4} asset='/assets/img/docs/payment_initiation/PIS-overview-row-no-downward-arrow.png' mdxType="StepWithDiagram">
  Using this access token your backend can now retrieve the user's bank account
  numbers via{' '}
  <a href='/docs/api/products/auth/#authget'>
    <code>/auth/get</code>
  </a>{' '}
  or their identity information (such as their full name) via{' '}
  <a href='/docs/api/products/identity/#identityget'>
    <code>/identity/get</code>
  </a>
  .
    </StepWithDiagram>
    <Layout.Spacer units={5} />
    <h4 {...{
      "id": "adding-the-auth-and-identity-products-to-your-app"
    }}>{`Adding the Auth and Identity products to your app`}</h4>
    <p>{`The steps listed above are broken down into more detail in the guides linked below. The first guide covers adding the `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/"
      }}>{`Auth`}</a>{` product (e.g. for obtaining the user's bank account numbers). The second covers the `}<a parentName="p" {...{
        "href": "/docs/api/products/identity/"
      }}>{`Identity`}</a>{` product (e.g. for obtaining the user's legal name). Both guides describe almost identical steps, so you can follow either one.`}</p>
    <p>{`The next section will highlight the single difference between the two and will show how you can use both products at the same time.`}</p>
    <Actions.Wrapper>
  <Actions.Card icon={<Docs mdxType="Docs" />} title='Auth' subtitle='Add Auth to your app' link='/docs/auth/add-to-app/' linkText='Detailed steps' />
  <Actions.Card icon={<Docs mdxType="Docs" />} title='Identity' subtitle='Add Identity to your app' link='/docs/identity/add-to-app/' linkText='Detailed steps' />
    </Actions.Wrapper>
    <h4 {...{
      "id": "creating-a-link-token"
    }}>{`Creating a Link token`}</h4>
    <p>{`Link token creation is the only step that requires changes when requesting both Auth and Identity-related information. To do so, pass both products as part of the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Create link token",
        "switcher": "false",
        "#Create": true,
        "link": true,
        "token": true
      }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "Plaid Test App",
  "user": { "client_user_id": "\${UNIQUE_USER_ID}" },
  "products": ["auth", "identity"], // using both products
  "country_codes": ["GB", "NL", "DE"],
  "language": "en",
  "webhook": "https://webhook.sample.com"
}'
`}</code></pre>
    <h4 {...{
      "id": "end-user-experience"
    }}>{`End user experience`}</h4>
    <p>{`During the Link flow, your user grants permission to all the data products that you request. The images below demonstrate examples of the panes Link will present when requesting access to one or multiple products.`}</p>
    <Grid noPadding noMarginRightSmall noMarginRightMedium noMarginRightLarge useThreadsGrid mdxType="Grid">
  <Column small={6} mdxType="Column">
    <Image src='/assets/img/docs/payment_initiation/ais-consent-pane-auth.png' alt="Plaid consent screen for sharing financial data with WonderWallet, featuring an 'Allow' button and 'Account details' section." mdxType="Image" />
  </Column>
  <Column small={6} mdxType="Column">
    <Image src='/assets/img/docs/payment_initiation/ais-consent-pane-auth-identity.png' alt="Plaid data sharing consent screen, listing Contact and Account details. Buttons: Allow, Cancel. Terms link present." mdxType="Image" />
  </Column>
    </Grid>
    <Layout.Spacer units={5} />
    <h3 {...{
      "id": "optional-steps-and-best-practices"
    }}>{`Optional steps and best practices`}</h3>
    <p>{`The sections below in this guide describe optional steps you can use to improve link conversion or achieve KYC/AML compliance.`}</p>
    <h4 {...{
      "id": "preselecting-a-financial-institution"
    }}>{`Preselecting a financial institution`}</h4>
    <p>{`By default, Plaid will ask the user to manually select their financial institution. However, there might be cases where you already know which institution your user wants to use. For example, when your user has previously completed a sign up flow, you can increase conversion by skipping this part of the payment flow. This requires two steps:`}</p>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/item/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` as an input parameter. This endpoint will return the `}<a parentName="li" {...{
          "href": "/docs/api/items/#item-get-response-item-institution-id"
        }}><inlineCode parentName="a">{`institution_id`}</inlineCode></a>{` as part of the response.`}</li>
      <li parentName="ol">{`Provide the `}<inlineCode parentName="li">{`institution_id`}</inlineCode>{` as part of the payment `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` request.`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Create link token with institution preselected",
        "switcher": "false",
        "#Create": true,
        "link": true,
        "token": true,
        "with": true,
        "institution": true,
        "preselected": true
      }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "Plaid Test App",
  "user": { "client_user_id": "\${UNIQUE_USER_ID}" },
  "user_id": "\${USER_ID}",
  "products": ["payment_initiation"],
  "country_codes": ["GB", "NL", "DE"],
  "language": "en",
  "webhook": "https://webhook.sample.com",
  "payment_initiation": { "payment_id": "\${PAYMENT_ID}" },
  "institution_id": "\${INSTITUTION_ID}" // preselect institution_id
}'
`}</code></pre>
    <h4 {...{
      "id": "compliant-account-funding"
    }}>{`Compliant account funding`}</h4>
    <p>{`To help comply with KYC & AML regulations you may choose to restrict payments to your app from bank accounts whose owner was previously verified.`}</p>
    <p>{`One-Time Payment Initiation can support this flow by requiring that a payment is to be made from a specific account. The process has three steps:`}</p>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` as an input parameter. This endpoint will return the user's account numbers as part of the response (Use `}<a parentName="li" {...{
          "href": "/docs/api/products/auth/#auth-get-response-numbers-bacs"
        }}><inlineCode parentName="a">{`bacs`}</inlineCode></a>{` for the UK and `}<a parentName="li" {...{
          "href": "/docs/api/products/auth/#auth-get-response-numbers-international"
        }}><inlineCode parentName="a">{`international`}</inlineCode></a>{` for the rest of Europe).`}</li>
      <li parentName="ol">{`Provide one of the account numbers under `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#payment_initiation-payment-create-request-options"
        }}><inlineCode parentName="a">{`options`}</inlineCode></a>{` as part of the `}<inlineCode parentName="li">{`/payment_initiation/payment/create`}</inlineCode>{` request. See the snippet below as an example.`}</li>
      <li parentName="ol">{`Provide the `}<inlineCode parentName="li">{`institution_id`}</inlineCode>{` as part of the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` request, as described in the previous section.`}</li>
    </ol>
    <Callout mdxType="Callout">
      <p>{`Not all financial institutions support this feature. In the UK there is full support. In the rest of Europe, support may be limited. Plaid will let the user choose any account when their financial institution does not support this feature. It is recommended to also verify the origin of payments as part of your payment reconciliation process.`}</p>
    </Callout>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Create payment request",
        "switcher": "false",
        "#Create": true,
        "payment": true,
        "request": true
      }}>{`curl -X POST https://sandbox.plaid.com/payment_initiation/payment/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "recipient_id": "\${RECIPIENT_ID}",
  "user_id": "\${USER_ID}",
  "reference": "Sample reference",
  "amount": {
    "currency": "GBP",
    "value": 1.99
  },
  "options": { // payer account restriction
    "bacs": {
        "account": "26207729",
        "sort_code": "560029"
    }
  }
}'
`}</code></pre>
    <h4 {...{
      "id": "compliant-withdrawals"
    }}>{`Compliant withdrawals`}</h4>
    <p>{`To comply with KYC and AML regulations, you may want to restrict outbound money movements to accounts whose owner is verified. You can do this in the following ways:`}</p>
    <h5 {...{
      "id": "using-virtual-accounts"
    }}>{`Using Virtual Accounts`}</h5>
    <p>{`Make sure your virtual account is set up before following these steps. For more information on setting up an account, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/managing-virtual-accounts/"
      }}>{`Managing virtual accounts`}</a>{`.`}</p>
    <ol>
      <li parentName="ol">{`Follow the `}<a parentName="li" {...{
          "href": "/docs/payment-initiation/virtual-accounts/payment-confirmation/"
        }}>{`Payment Confirmation flow`}</a>{` to confirm that funds have settled in your virtual account.`}</li>
      <li parentName="ol">{`Fetch the payment object by calling `}<inlineCode parentName="li">{`/payment_initiation/payment/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`payment_id`}</inlineCode>{` of the payment you initiated. The payment object will contain a `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#payment_initiation-payment-get-response-transaction-id"
        }}><inlineCode parentName="a">{`transaction_id`}</inlineCode></a>{`, which corresponds to the payment's underlying virtual account transaction.`}</li>
      <li parentName="ol">{`Fetch the virtual account transaction by calling `}<inlineCode parentName="li">{`/wallet/transaction/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`transaction_id`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`The virtual account transaction will contain account details in the `}<inlineCode parentName="li">{`counterparty`}</inlineCode>{` field.`}</li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "/docs/payment-initiation/virtual-accounts/payouts/"
        }}>{`Execute a Payout`}</a>{` using the `}<inlineCode parentName="li">{`counterparty`}</inlineCode>{` details.`}</li>
    </ol>
    <h5 {...{
      "id": "using-auth-data"
    }}>{`Using Auth data`}</h5>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/auth/get`}</inlineCode>{` with the `}<inlineCode parentName="li">{`access_token`}</inlineCode>{` as an input parameter. This endpoint will return the user's verified account numbers as part of the response (Use `}<a parentName="li" {...{
          "href": "/docs/api/products/auth/#auth-get-response-numbers-bacs"
        }}><inlineCode parentName="a">{`bacs`}</inlineCode></a>{` for the UK and `}<a parentName="li" {...{
          "href": "/docs/api/products/auth/#auth-get-response-numbers-international"
        }}><inlineCode parentName="a">{`international`}</inlineCode></a>{` for the rest of Europe).`}</li>
      <li parentName="ol">{`Select accounts that can receive funds by filtering for `}<inlineCode parentName="li">{`account.subtype = "checking"`}</inlineCode>{`.`}</li>
      <li parentName="ol">{`Initiate the withdrawal to any of the filtered accounts. You may want to let the user choose a preferred account in case there are multiple.`}</li>
    </ol>
    <h5 {...{
      "id": "using-the-request_refund_details-flag"
    }}>{`Using the `}<inlineCode parentName="h5">{`request_refund_details`}</inlineCode>{` flag`}</h5>
    <p>{`This approach is not recommended since it is an optional flag with limited, incomplete coverage dependent on the financial institution a payment was initiated to.`}</p>
    <ol>
      <li parentName="ol">{`Go through the `}<a parentName="li" {...{
          "href": "/docs/payment-initiation/payment-initiation-one-time"
        }}>{`One-Time Payment Initiation flow`}</a>{` and initiate a payment with the `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#payment_initiation-payment-create-request-options-request-refund-details"
        }}><inlineCode parentName="a">{`request_refund_details`}</inlineCode></a>{` flag enabled.`}</li>
      <li parentName="ol">{`Fetch the payment object by calling `}<inlineCode parentName="li">{`/payment_initiation/payment/get`}</inlineCode>{` and parse the `}<a parentName="li" {...{
          "href": "/docs/api/products/payment-initiation/#payment_initiation-payment-get-response-refund-details"
        }}><inlineCode parentName="a">{`refund_details`}</inlineCode></a>{` field.`}</li>
      <li parentName="ol">{`Initiate the withdrawal to the account specified in the `}<inlineCode parentName="li">{`refund_details`}</inlineCode>{` field.`}</li>
    </ol>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "before-you-get-started",
        level: 4,
        title: "Before you get started",
      },
{
        id: "user-onboarding",
        level: 3,
        title: "User onboarding",
      },
{
        id: "adding-the-auth-and-identity-products-to-your-app",
        level: 4,
        title: "Adding the Auth and Identity products to your app",
      },
{
        id: "creating-a-link-token",
        level: 4,
        title: "Creating a Link token",
      },
{
        id: "end-user-experience",
        level: 4,
        title: "End user experience",
      },
{
        id: "optional-steps-and-best-practices",
        level: 3,
        title: "Optional steps and best practices",
      },
{
        id: "preselecting-a-financial-institution",
        level: 4,
        title: "Preselecting a financial institution",
      },
{
        id: "compliant-account-funding",
        level: 4,
        title: "Compliant account funding",
      },
{
        id: "compliant-withdrawals",
        level: 4,
        title: "Compliant withdrawals",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
